import { useContext, useEffect, useState } from 'react'
import { Link, useOutletContext } from 'react-router-dom'
import AuthContext from 'contexte/AuthContext'
import PageLoader from 'components/pageLoader/PageLoader'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import { launchError, launchSuccess } from 'components/notifications'
import { fetchSpIssues, recomputeSpPercentages } from 'services/structuredProductsServices'
import BBookEmpty from 'pages/bBook/components/BBookEmpty'
import TermSheetUploadButton from './components/TermSheetUploadButton'
import LoadCouponsModal from './components/LoadCouponsModal'
import { formatParisDate, formatAmount } from './components/spDisplay'

// Tronqué à 2 décimales, jamais arrondi au-dessus : 99,99778 % s'affiche 99,99 %, pas 100,00 %
const truncatePercent = (value) => Math.floor(Math.round(Number(value || 0) * 1e5) / 1e3) / 100
const formatPercent = (value) => `${truncatePercent(value).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`

// Produits fermés à traiter : term sheet ou coupons manquants, % des invests non ajustés au total, total sous l'investi
const SpIssues = () => {
  const { user } = useContext(AuthContext)
  const { setIssuesCount } = useOutletContext()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [recomputing, setRecomputing] = useState(null)
  const [couponsProductId, setCouponsProductId] = useState(null)

  const fetchProducts = async () => {
    try {
      setLoading(true)
      const data = await fetchSpIssues({ user })
      setProducts(data)
      setIssuesCount(data.length)
    } catch (error) {
      launchError('Erreur lors du chargement des produits à traiter')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const recompute = async (product) => {
    if (!window.confirm(`Recalculer le % des ${product.invests_count} investissements de « ${product.name} » sur un total de ${formatAmount(product.total)} € ?`)) return
    try {
      setRecomputing(product._id)
      const result = await recomputeSpPercentages({ user, productId: product._id })
      launchSuccess(`${result.invests_updated} investissements recalculés (somme : ${formatPercent(result.percentage_sum)})`)
      fetchProducts()
    } catch (error) {
      launchError(error?.response?.data?.error === 'total_below_invested'
        ? "Le total est inférieur au montant investi : corrigez le total avant de recalculer"
        : 'Erreur lors du recalcul des pourcentages')
    } finally {
      setRecomputing(null)
    }
  }

  const tbody = products.map(product => {
    const missingCoupons = !product.has_term_sheet || !product.coupons_count
    const wrongPercentages = product.invests_wrong_percentage > 0
    return {
      data: [
        <Link to={`/structured-products/product/${product._id}/closed`}>{product.name}</Link>,
        formatParisDate(product.collecting_date),
        <span className='b_book_num'>{formatAmount(product.invested)} / {formatAmount(product.total)} €</span>,
        <span className='b_book_num'>
          {formatPercent(product.percentage_sum)}
          {wrongPercentages && <span className='sp_muted'> (attendu {formatPercent(product.expected_percentage_sum)})</span>}
        </span>,
        <div className='sp_reasons'>
          {!product.has_term_sheet && <span className='sp_reason'>Term sheet manquant</span>}
          {!product.coupons_count && <span className='sp_reason'>Aucun coupon</span>}
          {wrongPercentages && <span className='sp_reason'>% non ajustés ({product.invests_wrong_percentage}/{product.invests_count})</span>}
          {product.total_below_invested && <span className='sp_reason'>Total inférieur à l'investi</span>}
        </div>,
        <div className='sp_actions'>
          {wrongPercentages && !product.total_below_invested && (
            <button className='b_book_row_button' disabled={recomputing === product._id} onClick={() => recompute(product)}>
              {recomputing === product._id ? 'Recalcul...' : 'Recalculer les %'}
            </button>
          )}
          {product.term_sheet_url && (
            <a className='b_book_row_button' href={product.term_sheet_url} target='_blank' rel='noreferrer'>Voir le term sheet</a>
          )}
          {product.has_term_sheet && !product.coupons_count && !product.total_below_invested && (
            <button className='b_book_row_button' onClick={() => setCouponsProductId(product._id)}>Charger les coupons</button>
          )}
          {missingCoupons && (
            <TermSheetUploadButton
              productId={product._id}
              className='b_book_row_button'
              label={product.has_term_sheet ? 'Remplacer le term sheet' : 'Ajouter le term sheet'}
              onUploaded={fetchProducts}
            />
          )}
        </div>,
      ],
    }
  })

  return (
    <>
      <div className='b_book_title_component'>
        <h1 className='b_book_page_title'>À traiter</h1>
      </div>
      <p className='b_book_hint b_book_page_lede'>
        Produits clôturés avec un problème : term sheet ou coupons manquants (l'ajout du term sheet crée les coupons), pourcentages des investissements non ajustés au total, ou total inférieur au montant investi.
      </p>

      <div className='b_book_card'>
        {loading ? (
          <div className='pageLoaderBox'><PageLoader /></div>
        ) : tbody.length ? (
          <div className='table-container'>
            <Tableau thead={['Nom', 'Collecte', 'Investi / Total', 'Somme des %', 'Problèmes', '']} tbody={tbody} />
          </div>
        ) : (
          <BBookEmpty title='Rien à traiter' hint='Tous les produits clôturés ont un term sheet, des coupons et des pourcentages ajustés.' />
        )}
      </div>
      <LoadCouponsModal
        productId={couponsProductId}
        isOpen={!!couponsProductId}
        onClose={() => setCouponsProductId(null)}
        onLoaded={fetchProducts}
      />
    </>
  )
}

export default SpIssues
